import React, { useState } from 'react';
import { 
  Terminal, 
  Copy, 
  Check, 
  Code2,
  Package,
  Cpu,
  Layers
} from 'lucide-react';

export const ApiDocs: React.FC = () => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      setCopiedKey(null);
      return;
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const pythonSdkSnippet = `# Install: pip install agenticscale
from agenticscale import AgenticScale
import os

client = AgenticScale(
    agent_id="agent-invoice-01",
    api_key=os.environ.get("AGENTICSCALE_KEY", "ags_demo_key"),
    base_url="https://agenticscale.org"
)

# Start a traceable task run
run = client.start_run(task="Process invoice INV-2048")

# In-flight guardrail evaluation before tool execution
decision = client.evaluate_action(
    action_name="send_wire_transfer",
    target_resource="wire://swift.fedwire/transfer",
    payload={"amount": 45000.00, "vendor_id": "V-9912"},
    prompt_input="Urgent memo: update bank account and transfer immediately",
    run_id=run["run_id"]
)

if decision["decision"] == "ALLOW":
    print("✓ Approved: executing tool...")
elif decision["decision"] == "REVIEW":
    print("⚠ Held for human review:", decision.get("reasons"))
else:
    print("🛑 Blocked by safety policy:", decision.get("reasons"))`;

  const tsSdkSnippet = `// Install: npm install @agenticscale/sdk
import { AgenticScaleClient } from '@agenticscale/sdk';

const client = new AgenticScaleClient({
  baseUrl: 'https://agenticscale.org',
  apiKey: process.env.AGENTICSCALE_KEY || 'ags_demo_key',
  agentId: 'agent-invoice-01',
  environment: 'production'
});

// Start a traceable run
const run = await client.startRun('Process invoice INV-2048');

// Evaluate tool action at runtime
const evaluation = await client.evaluateAction({
  actionName: 'send_wire_transfer',
  targetResource: 'wire://swift.fedwire/transfer',
  payload: { amount: 45000.00, vendor_id: 'V-9912' },
  promptInput: 'Urgent memo: update bank account and transfer immediately',
  runId: run.run_id
});

if (evaluation.decision === 'ALLOW') {
  console.log('✓ Action approved');
} else if (evaluation.decision === 'REVIEW') {
  console.warn('⚠ Held for dual-approval:', evaluation.reasons);
} else {
  console.error('🛑 Blocked by safety policy:', evaluation.reasons);
}`;

  const curlSnippet = `curl -X POST https://agenticscale.org/api/gateway/evaluate \\
  -H "Content-Type: application/json" \\
  -H "X-AgenticScale-Key: $AGENTICSCALE_KEY" \\
  -d '{
    "agent_id": "agent-invoice-01",
    "action_name": "update_vendor_account",
    "target_resource": "vendor_db:id_9941",
    "payload": {
      "amount": 45000.00,
      "vendor_account_changed": true
    },
    "prompt_input": "Urgent request from supplier to update bank details"
  }'`;

  const telemetrySnippet = `# Start a traceable run
curl -X POST https://agenticscale.org/api/telemetry/runs \\
  -H "Content-Type: application/json" \\
  -H "X-AgenticScale-Key: $AGENTICSCALE_KEY" \\
  -d '{"agent_id":"agent-invoice-01","task":"Process invoice INV-8812"}'

# Append a redacted tool event
  curl -X POST https://agenticscale.org/api/telemetry/events \\
  -H "Content-Type: application/json" \\
  -H "X-AgenticScale-Key: $AGENTICSCALE_KEY" \\
  -d '{"agent_id":"agent-invoice-01","trace_id":"trace-123","event_type":"tool_call","tool_name":"invoice_reader","payload":{"invoice_id":"INV-8812"}}'`;

  const mcpSnippet = `{
  "mcpServers": {
    "agenticscale": {
      "url": "https://agenticscale.org/api/mcp",
      "headers": {
        "X-AgenticScale-Key": "ags_your_scoped_key"
      }
    }
  }
}`;

  return (
    <div className="space-y-8 animate-fade-in max-w-6xl mx-auto">
      
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
          Safety Gateway API & SDKs
        </h1>
        <p className="text-slate-600 dark:text-slate-400 text-sm mt-1.5 max-w-3xl">
          One HTTP contract, so an agent written in anything can call it. The Python and TypeScript clients
          below are real; framework-specific middleware is not something AgenticScale ships.
        </p>
      </div>

      {/* Package Quick Install Banners */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="glass-panel p-5 rounded-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400">
              <Package className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-900 dark:text-white">Node.js / TypeScript SDK</div>
              <code className="text-[11px] font-mono text-slate-500 dark:text-slate-400">npm install @agenticscale/sdk</code>
            </div>
          </div>
          <button
            onClick={() => copyToClipboard('npm install @agenticscale/sdk', 'npm_cmd')}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
          >
            {copiedKey === 'npm_cmd' ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>

        <div className="glass-panel p-5 rounded-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
              <Cpu className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-900 dark:text-white">Python PyPI Package</div>
              <code className="text-[11px] font-mono text-slate-500 dark:text-slate-400">pip install agenticscale</code>
            </div>
          </div>
          <button
            onClick={() => copyToClipboard('pip install agenticscale', 'pip_cmd')}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
          >
            {copiedKey === 'pip_cmd' ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="glass-panel space-y-3 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
              <Layers className="h-4 w-4 text-brand-600 dark:text-brand-400" />
              <span>Central Telemetry API</span>
            </span>
            <button onClick={() => copyToClipboard(telemetrySnippet, 'telemetry')} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 dark:border-slate-700 dark:text-slate-200">
              {copiedKey === 'telemetry' ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />} 
              <span>{copiedKey === 'telemetry' ? 'Copied' : 'Copy'}</span>
            </button>
          </div>
          <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">Account integrations can create runs and append redacted events. Public demo access remains read-only.</p>
          <pre className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900 p-4 text-[11px] leading-relaxed text-slate-100">{telemetrySnippet}</pre>
        </div>

        <div className="glass-panel space-y-3 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
              <Code2 className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              <span>Model Context Protocol (MCP)</span>
            </span>
            <button onClick={() => copyToClipboard(mcpSnippet, 'mcp')} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 dark:border-slate-700 dark:text-slate-200">
              {copiedKey === 'mcp' ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />} 
              <span>{copiedKey === 'mcp' ? 'Copied' : 'Copy'}</span>
            </button>
          </div>
          <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">Add to Claude Desktop or Cursor configuration to enforce AgenticScale guardrails on AI tools.</p>
          <pre className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900 p-4 text-[11px] leading-relaxed text-slate-100">{mcpSnippet}</pre>
        </div>
      </div>

      {/* Code Snippets */}
      <div className="space-y-6">
        
        {/* Python */}
        <div className="glass-panel p-6 rounded-2xl space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-900 dark:text-white flex items-center space-x-2">
              <Code2 className="w-4 h-4 text-brand-600 dark:text-brand-400" />
              <span>Python SDK</span>
            </span>
            <button
              onClick={() => copyToClipboard(pythonSdkSnippet, 'python')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 flex items-center space-x-1.5 shadow-sm"
            >
              {copiedKey === 'python' ? <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copiedKey === 'python' ? 'Copied' : 'Copy Python'}</span>
            </button>
          </div>
          <pre className="p-4 rounded-xl bg-slate-900 dark:bg-slate-950 text-slate-100 font-mono text-xs overflow-x-auto leading-relaxed border border-slate-800">
            {pythonSdkSnippet}
          </pre>
        </div>

        {/* TypeScript */}
        <div className="glass-panel p-6 rounded-2xl space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-900 dark:text-white flex items-center space-x-2">
              <Code2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span>TypeScript / Node.js SDK</span>
            </span>
            <button
              onClick={() => copyToClipboard(tsSdkSnippet, 'ts')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 flex items-center space-x-1.5 shadow-sm"
            >
              {copiedKey === 'ts' ? <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copiedKey === 'ts' ? 'Copied' : 'Copy TypeScript'}</span>
            </button>
          </div>
          <pre className="p-4 rounded-xl bg-slate-900 dark:bg-slate-950 text-slate-100 font-mono text-xs overflow-x-auto leading-relaxed border border-slate-800">
            {tsSdkSnippet}
          </pre>
        </div>

        {/* cURL */}
        <div className="glass-panel p-6 rounded-2xl space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-900 dark:text-white flex items-center space-x-2">
              <Terminal className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <span>cURL Direct REST Evaluation</span>
            </span>
            <button
              onClick={() => copyToClipboard(curlSnippet, 'curl')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 flex items-center space-x-1.5 shadow-sm"
            >
              {copiedKey === 'curl' ? <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copiedKey === 'curl' ? 'Copied' : 'Copy cURL'}</span>
            </button>
          </div>
          <pre className="p-4 rounded-xl bg-slate-900 dark:bg-slate-950 text-slate-100 font-mono text-xs overflow-x-auto leading-relaxed border border-slate-800">
            {curlSnippet}
          </pre>
        </div>

      </div>

    </div>
  );
};
